import { useApiPost, useApiQuery } from '@/api/main/query'
import { usePaymentRequisitActions } from '@/entities/paymentRequisit'
import { EnrichedRequisitesTable } from '@/features/baseTable/ui/EnrichedRequisitesTable'
import {
  DialogEditRequisit,
  RequisiteMobileBlock,
  RequisitesHeader,
  detailChangeStatus,
  invalidateAllRequisiteData,
  requisitColumns,
  requisiteAPI,
  useEditRequisit,
  RequisiteModel,
  UpdateRequisitesStatus, EnrichedRequisitesModel,
} from '@/features/requisites'
import { DEFAULT_TAKE } from '@/shared/constants/constants'
import { useDebounce, useToast } from '@/shared/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { Table } from '@tanstack/react-table'
import { useCallback, useEffect, useState } from 'react'
import { BaseTable, BaseTableSkeleton } from 'syncra-storybook/features'
import { AdminRequisitForFarmListPayloadListResult } from 'syncra-api-client'

type Props = {
  startDate?: string
  endDate?: string
  farmId?: string
}

const detailFilterButtons = [
  { name: 'Все', label: 'all', index: 0 },
  { name: 'Активные', label: 'active', index: 1 },
  { name: 'Приостановленые', label: 'disabled', index: 2 },
  { name: 'Заблокированные', label: 'blocked', index: 4 },
]

export const FarmRequisitesTable = ({ farmId, startDate, endDate }: Props) => {
  const [search, setSearch] = useState('')
  const [selectedBtn, setSelectedBtn] = useState('all')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [requisiteStatus, setRequisiteStatus] = useState(1)
  const [modalEditRequisiteVisible, setModalEditRequisiteVisible] = useState(false)
  const [activePaymentRequisite, setActivePaymentRequisite] = useState<string>('')
  const [requisitId, setRequisitId] = useState('')
  const [skip, setSkip] = useState(0)
  const [tableInstance, setTableInstance] = useState<Table<RequisiteModel> | null>(null)
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null)
  const debouncedSearch = useDebounce(search, 500)
  const debouncedSkip = useDebounce(skip, 500)
  const effectiveSkip = skip === 0 ? 0 : debouncedSkip

  const queryClient = useQueryClient()
  const { addToast } = useToast()

  const toggleRowSelection = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleSuccessAction = useCallback(() => {
    setModalEditRequisiteVisible(false)
    setRequisitId('')
  }, [])

  const {
    data: requisitesData,
    error: requisitesError,
    isLoading: requisitesIsLoad,
    refetch,
  } = useApiQuery<AdminRequisitForFarmListPayloadListResult>(
    ['requisites', effectiveSkip, DEFAULT_TAKE, debouncedSearch, requisiteStatus, farmId],
    requisiteAPI.farm,
    {
      skip: effectiveSkip,
      take: DEFAULT_TAKE,
      farmId,
      startDate,
      endDate,
      status: requisiteStatus,
      search: debouncedSearch,
    }
  )

  useEffect(() => {
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, requisiteStatus, debouncedSearch, skip])

  const enrichedData = (requisitesData?.items ?? []).map((req) => ({
    ...req.requisit,
    paymentRequisitProfiles: req.paymentRequisits,
    dailyStats: [{ amountInRub: req?.dailyRequisitStatistic?.amountInRub }],
    totalStats: [
      {
        counterIn: req?.topupAggregate?.totalCount,
        counterInSuccess: req?.topupAggregate?.paidCount,
        amountInRub: req?.topupAggregate?.paidFactAmountFrom,
      },
    ],
  }))
  const {
    data: queryRequisitData,
    error: requisitError,
    refetch: refetchRequisitData,
  } = useApiQuery<RequisiteModel>(
    ['requisit', requisitId],
    requisiteAPI.getRequisit(requisitId),
    undefined,
    {
      enabled: false,
    }
  )

  const { disablePaymentRequisit, enablePaymentRequisit } = usePaymentRequisitActions(
    [],
    activePaymentRequisite
  )

  const { handleDeleteRequisit} = useEditRequisit(
    queryRequisitData,
    requisitId,
    handleSuccessAction
  )

  const { mutate: updateRequsitesStatus } = useApiPost<
    void,
    {
      guids: string[]
      status: UpdateRequisitesStatus
      reason?: string
    }
  >(requisiteAPI.updateRequsitesStatus, {
    onSuccess: () => {
      addToast('Успех!', 'Статус реквизитов успешно обновлен.', 'success')
      invalidateAllRequisiteData(queryClient, requisitId)

      setSelectedIds(new Set())
      tableInstance?.resetRowSelection()
    },
    onError: (error) => {
      addToast('Ошибка!', error.message, 'error')
    },
  })

  useEffect(() => {
    if (requisitesError) {
      addToast('Ошибка!', requisitesError.message, 'error')
    }
  }, [requisitesError])

  useEffect(() => {
    if (requisitId && requisitId.trim() !== '') {
      refetchRequisitData().then((res) => {
        if (res.data) {
          setModalEditRequisiteVisible(true)
        }
      })
    }
  }, [requisitId, refetchRequisitData])

  const handleOpenModal = (id: string) => {
    if (!id || id.trim() === '') {
      addToast('Ошибка!', 'Некорректный ID реквизита', 'error')
      return
    }
    setRequisitId(id)
  }
  useEffect(() => {
    if (requisitError) {
      addToast('Ошибка!', requisitError.message, 'error')
    }
  }, [requisitError])

  const handleCloseModal = (value: boolean) => {
    setModalEditRequisiteVisible(value)
    setRequisitId('')
  }

  const handleDisablePaymentRequisit = (id: string) => {
    setActivePaymentRequisite(id)
    disablePaymentRequisit(id)
  }

  const handleEnablePaymentRequisit = (id: string) => {
    setActivePaymentRequisite(id)
    enablePaymentRequisit(id)
  }

  const valueToIndexMap = Object.fromEntries(
    detailChangeStatus.map((opt) => [opt.value, opt.index])
  )

  const handleUpdateStatus = (value: string) => {
    const index = valueToIndexMap[value]
    if (index === undefined) return

    const selectedFromTable =
      tableInstance?.getSelectedRowModel().rows.map((row) => row.original.id) || []
    const selectedMobile = Array.from(selectedIds)
    const selected = selectedFromTable.length > 0 ? selectedFromTable : selectedMobile

    if (!selected.length) {
      addToast('Ошибка!', 'Сначала выберите хотя бы один реквизит', 'error')
      setSelectedStatus(null)
      return
    }

    updateRequsitesStatus({ guids: selected, status: index })
    setSelectedStatus(null)
  }

  return (
    <div className="flex flex-col gap-4">
      <RequisitesHeader
        selectedBtn={selectedBtn}
        selectFilterBtn={(label) => {
          setSelectedBtn(label)
          const selectedButton = detailFilterButtons.find((b) => b.label === label)
          if (selectedButton) {
            setRequisiteStatus(selectedButton.index)
          }
        }}
        setSearch={setSearch}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        onChangeStatus={(value) => {
          if (typeof value === 'string') {
            handleUpdateStatus(value)
          }
        }}
        setSkip={setSkip}
      />

      {requisitesError ? (
        <p>Ошибка загрузки данных.</p>
      ) : requisitesIsLoad ? (
        <BaseTableSkeleton />
      ) : !requisitesData?.items || requisitesData.items.length === 0 ? (
        <p>Реквизитов нет.</p>
      ) : (
        <BaseTable
          columns={requisitColumns({ onEdit: handleOpenModal })}
          data={enrichedData as unknown as EnrichedRequisitesModel[]}
          onPageChange={(newSkip) => setSkip(newSkip)}
          cardVariant="primary"
          cardClasses="border-none rounded-none flex flex-col gap-4"
          skip={skip}
          totalItems={requisitesData.count}
          isRowLink
          buildRowUrl={(id: string) => `/requisites/${id}`}
          navigateTo="requisites"
          onTableReady={(table) => setTableInstance(table)}
          mobileCards={(enrichedData as unknown as EnrichedRequisitesModel[]).map((detail) => (
            <RequisiteMobileBlock
              key={detail.id}
              requisit={detail}
              isSelected={selectedIds.has(detail.id)}
              onSelect={() => toggleRowSelection(detail.id)}
              onEdit={handleOpenModal}
            />
          ))}
          expandableRows
          expandableChildren={(row) => (
            <EnrichedRequisitesTable
              requisit={row}
              onDisable={handleDisablePaymentRequisit}
              onEnable={handleEnablePaymentRequisit}
            />
          )}
        />
      )}
      {modalEditRequisiteVisible && (
        <DialogEditRequisit
          modalVisible={modalEditRequisiteVisible}
          setModalVisible={handleCloseModal}
          onSubmit={() => handleCloseModal(false)}
          onDelete={handleDeleteRequisit}
          requisitData={queryRequisitData}
        />
      )}
    </div>
  )
}
